<template>
  <div class="formily-reason-selector">
    <div v-for="(option, index) in options" :key="option.value" class="reason-item">
      <!-- Checkbox -->
      <div class="reason-checkbox">
        <CheckboxView
          v-model="checkboxValues[index]"
          :option="option"
          :disabled="disabled || readonly"
          @change="toggleReason(option, index)"
        />
      </div>

      <!-- 如果有 remark 字段且被选中,显示输入框 -->
      <div v-if="hasRemark(option) && isSelected(option.value)" class="reason-input">
        <rich-textarea-box
          v-model="remarksMap[option.value]"
          :showlength="!readonly && !disabled"
          showlength-position="inner"
          :maxlength="option.maxlength || 200"
          :immediate-input="false"
          :placeholder="option.placeholder"
          :disabled="disabled || readonly"
          :class="{ 'has-error': hasError(option) }"
          @input="handleRemarkChange(option.value, $event)"
        />
        <!-- 错误提示 -->
        <div v-if="hasError(option)" class="error-message">
          {{ $t('Gattaran_form_MLcA_tPgo' /** 该字段是必填字段 */) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CheckboxView } from '@/formily/src'

export default {
  name: 'ReasonSelectorView',
  components: {
    CheckboxView,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      default: () => [],
    },
    // 只读模式
    readonly: {
      type: Boolean,
      default: false,
    },
    // 禁用模式
    disabled: {
      type: Boolean,
      default: false,
    },
    // 是否触发验证
    shouldValidate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      checkboxValues: [], // CheckboxView 的 v-model 数组
      selectedReasons: [],
      remarksMap: {}, // 存储每个选项的补充说明
    }
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal && Array.isArray(newVal)) {
          this.selectedReasons = newVal.map(item => item.key)
          // 恢复备注信息
          const newRemarksMap = {}
          newVal.forEach(item => {
            if (item.remark !== undefined) {
              newRemarksMap[item.key] = item.remark
            }
          })
          this.remarksMap = newRemarksMap
          // 更新 CheckboxView 的值
          this.updateCheckboxValues()
        }
      },
      immediate: true,
    },
    options: {
      handler() {
        this.updateCheckboxValues()
      },
      immediate: true,
    },
  },
  methods: {
    updateCheckboxValues() {
      // 根据 selectedReasons 更新 checkboxValues
      this.checkboxValues = this.options.map(option => {
        return this.selectedReasons.includes(option.value) ? option.value : false
      })
    },
    hasRemark(option) {
      // 判断是否有 remark 字段(即需要输入框)
      return Object.prototype.hasOwnProperty.call(option, 'remark')
    },
    hasError(option) {
      // 只读或禁用模式不显示错误
      if (this.readonly || this.disabled) return false
      // 非必填选项不显示错误
      if (!option.required) return false
      // 检查是否选中了该选项
      if (!this.isSelected(option.value)) return false

      // 检查是否为空
      const remark = this.remarksMap[option.value]
      return remark === null || remark === undefined || remark.trim() === ''
    },
    isSelected(value) {
      return this.selectedReasons.includes(value)
    },
    toggleReason(option, index) {
      // 如果是只读或禁用模式,不允许切换
      if (this.readonly || this.disabled) return

      const newValue = this.checkboxValues[index]

      if (newValue) {
        // 选中,添加到 selectedReasons
        if (!this.selectedReasons.includes(option.value)) {
          this.selectedReasons.push(option.value)
        }
        // 如果有 remark 字段,初始化备注
        if (this.hasRemark(option)) {
          this.$set(this.remarksMap, option.value, '')
        }
      } else {
        // 取消选中,从 selectedReasons 移除
        const idx = this.selectedReasons.indexOf(option.value)
        if (idx > -1) {
          this.selectedReasons.splice(idx, 1)
        }
        // 清除对应的备注
        this.$delete(this.remarksMap, option.value)
      }

      this.emitChange()
    },
    handleRemarkChange(key, value) {
      // 如果是只读或禁用模式,不允许修改
      if (this.readonly || this.disabled) return

      this.$set(this.remarksMap, key, value)
      this.emitChange()
    },
    emitChange() {
      // 构造返回数据: [{ key, remark }]
      const result = this.selectedReasons.map(key => {
        const option = this.options.find(opt => opt.value === key)
        const reasonItem = { key }
        // 如果该选项有 remark 字段,添加 remark
        if (option && this.hasRemark(option)) {
          reasonItem.remark = this.remarksMap[key] || ''
        }
        return reasonItem
      })
      this.$emit('input', result)
      this.$emit('change', result)
    },
  },
}
</script>

<style lang="scss" scoped>
.formily-reason-selector {
  .reason-item {
    margin-top: 8px;

    margin-bottom: 32px;
    &:first-child {
      margin-top: 48px;
    }

    &:last-child {
      margin-bottom: 0;
    }

    .reason-checkbox {
      ::v-deep .formily-checkbox.rich-checkbox {
        padding: 16px 0;
      }
    }

    .reason-input {
      margin-top: 16px;

      ::v-deep .rich-textarea-box {
        width: 100%;
        min-height: 120px;
        max-height: 120px;
        background: #f5f5f7;
        border-radius: 40px;
        opacity: 1;
        padding: 16px;
        font-size: 28px;
        border: none;
        overflow: hidden;

        // 去掉默认边框
        &::after {
          display: none !important;
          border: none !important;
        }

        .rich-textarea {
          background: transparent;
          font-size: 28px;
          line-height: 40px;
          color: #282b33;
          border: none;
          outline: none;
          resize: none;
          width: 100%;
          max-height: 88px;
          overflow-y: auto;
        }
      }

      // 错误状态样式
      ::v-deep .rich-textarea-box.has-error {
        background: #fff;
        border: 2px solid #ff4d4f;
      }

      // 错误提示样式
      .error-message {
        font-size: 24px;
        color: #ff4d4f;
        line-height: 32px;
        margin-top: 8px;
        margin-left: 16px;
      }

      // 同时去掉 wrapper 的边框
      ::v-deep .rich-textarea-box-wrapper {
        &::after {
          display: none !important;
          border: none !important;
        }
      }
    }
  }
}
</style>
